import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, ShoppingBag, Compass, Navigation, User, Bell, MapPin, LogOut, Shield, PiggyBank, Receipt } from "lucide-react";
import { Logo } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV = [
  { label: "Home", icon: Home, route: "/", testId: "nav-item-home" },
  { label: "My List", icon: ShoppingBag, route: "/list", testId: "nav-item-my-list" },
  { label: "Discover", icon: Compass, route: "/discover", testId: "nav-item-discover" },
  { label: "Savings", icon: PiggyBank, route: "/savings", testId: "nav-item-savings" },
  { label: "Profile", icon: User, route: "/profile", testId: "nav-item-profile" },
];

export default function Layout({ children }) {
  const { user, prefs, logout } = useAuth();
  const navigate = useNavigate();
  const city = prefs?.city || "Set location";
  const initials = (user?.name || "G").split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-pp-cream">
      {/* Desktop / top header */}
      <header className="sticky top-0 z-50 h-16 border-b border-pp-border bg-pp-cream/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Logo size={38} />
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink key={n.route} to={n.route} end={n.route === "/"} data-testid={`top-${n.testId}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${isActive ? "bg-pp-forest text-white" : "text-pp-text hover:bg-white"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button data-testid="location-chip" onClick={() => navigate("/profile")}
              className="hidden sm:flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-pp-text pp-shadow hover:scale-[1.03] transition-transform">
              <MapPin className="h-4 w-4 text-pp-forest" /> {city}
            </button>
            <button data-testid="notifications-button" onClick={() => navigate("/alerts")}
              className="relative rounded-full bg-white p-2 pp-shadow hover:scale-105 transition-transform">
              <Bell className="h-5 w-5 text-pp-text" />
              <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-pp-gold" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="profile-avatar-button" className="rounded-full outline-none">
                  <Avatar className="h-9 w-9 border-2 border-pp-fresh">
                    <AvatarFallback className="bg-pp-forest text-white text-sm font-bold">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-semibold text-pp-text truncate">{user?.name}</p>
                  <p className="text-xs text-pp-muted truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User className="h-4 w-4 mr-2" /> Profile & Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/pantry")} data-testid="menu-pantry">
                  <ShoppingBag className="h-4 w-4 mr-2" /> My Pantry
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/trips")} data-testid="menu-trips">
                  <Navigation className="h-4 w-4 mr-2" /> Trip history
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/receipts")} data-testid="menu-receipts">
                  <Receipt className="h-4 w-4 mr-2" /> Receipts
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <Shield className="h-4 w-4 mr-2" /> Admin Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-pp-error">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-28 lg:pb-10">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 border-t border-pp-border bg-white/95 backdrop-blur-md pp-shadow-lg pb-[env(safe-area-inset-bottom)]">
        <div className="grid grid-cols-5">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.route} to={n.route} end={n.route === "/"} data-testid={n.testId}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] font-semibold transition-colors ${isActive ? "text-pp-forest" : "text-pp-muted"}`}>
                {({ isActive }) => (
                  <>
                    <span className={`rounded-full p-1.5 transition-colors ${isActive ? "bg-[#EBF7EE]" : ""}`}>
                      <Icon className="h-5 w-5" />
                    </span>
                    {n.label}
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
